// frontend/src/pages/GroupsPage.tsx - Enhanced with deactivate/delete and assignments
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// Use entity-specific hooks
import {
  useGroups,
  useGroup,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useGroupMembers,
  useAddGroupMembers,
  useRemoveGroupMember,
  useLeaveGroup
} from '@/hooks/useGroups';

import { useOrganizations } from '@/hooks/useOrganizations';
import { useUsers } from '@/hooks/useUsers';

// Import API types
import type {
  Group,
  Organization,
  User,
  NormalizedUser,
  GroupsQueryParams,
  GroupCreateRequest,
  GroupUpdateRequest,
  AddMembersRequest,
  UserRole
} from '@/api/types';

// ===== INTERFACES =====

interface OrganizationStrategy {
  mode: 'organization_specific' | 'single_organization';
  canSelectOrganization: boolean;
  defaultOrganization: string | undefined;
}

interface GroupFormData {
  name: string;
  type: 'visibility' | 'approval';
  description: string;
  isActive: boolean;
}

interface GroupAssignment {
  _id: string;
  name: string;
}

interface EnhancedGroup extends Group {
  assignedProjects?: GroupAssignment[];
  assignedWorkflows?: Array<{
    _id: string;
    name: string;
    stages: Array<{
      stageName: string;
      role: string;
    }>;
  }>;
}

// ===== UTILITY FUNCTIONS =====

function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class GroupsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('GroupsPage Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="text-center py-8">
          <p className="text-red-600">Something went wrong loading the groups page.</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== MEMBER MANAGEMENT MODAL =====

const MemberManagementModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  group?: Group;
  organizationId?: string;
  onRefetchGroups?: () => void;
}> = ({ isOpen, onClose, group, organizationId, onRefetchGroups }) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { setGlobalError, clearGlobalError } = useAppStore();
  const { user: currentUser } = useAuthStore();

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Only fetch data when modal is open and we have valid IDs
  const shouldLoadData = isOpen && !!organizationId && organizationId.trim() !== '' && !!group;

  // ✅ Fetch the specific group data to always have fresh member information
  const groupId = group?._id || group?.id;
  const {
    data: groupData,
    refetch: refetchGroup
  } = useGroup(
    shouldLoadData && organizationId ? organizationId : undefined,
    shouldLoadData && groupId ? groupId : undefined,
    {
      immediate: shouldLoadData && !!groupId && !!organizationId
    }
  );

  // Use fresh group data from the hook, fallback to prop if not loaded yet
  const currentGroup = groupData || group;

  // Use useUsers for fetching all organization users (for adding new members)
  const {
    data: usersData,
    loading: usersLoading,
    refetch: refetchUsers
  } = useUsers(
    shouldLoadData ? {
      organizationId: organizationId!,
      search: debouncedSearchTerm || undefined,
      limit: 100
    } : undefined,
    {
      immediate: shouldLoadData
    }
  );

  // Current members come from the group itself. GET /groups/:id populates
  // them (username, role), so no lookup is needed. They used to be derived
  // from the USER SEARCH results — so typing a name to find someone to add
  // made the existing members vanish from the list, and anyone beyond the
  // first 100 users never appeared at all (customer report, 2026-09-28).
  const currentMembers = useMemo((): User[] => {
    const raw: any[] = Array.isArray(currentGroup?.members) ? (currentGroup!.members as any[]) : [];
    if (raw.length === 0) return [];
    const populated = raw.filter(m => m && typeof m === 'object' && (m._id || m.id));
    if (populated.length === raw.length) return populated as User[];

    // Ids only (an older payload): resolve what we can from the loaded users.
    const byId = new Map<string, User>(
      (usersData?.data || []).map((u: User) => [String(u._id || u.id), u])
    );
    return raw
      .map(m => String(typeof m === 'string' ? m : (m?._id || m?.id || '')))
      .filter(Boolean)
      .map(id => byId.get(id) || ({ _id: id, username: 'Unknown user', role: '' } as unknown as User));
  }, [currentGroup?.members, usersData?.data]);

  // Refetch both users and the specific group data
  const refetchMembers = useCallback(() => {
    refetchUsers();
    refetchGroup();
  }, [refetchUsers, refetchGroup]);

  // Security filter for users - only show users from same organization for non-implementors
  const secureUsers = useMemo(() => {
    if (!usersData?.data || !Array.isArray(usersData.data)) {
      return [];
    }

    const filteredUsers = usersData.data.filter((user: User) => {
      // For implementors, show all users from the requested organization
      if (currentUser?.role === 'implementor') {
        return true;
      }

      // For non-implementors, ensure user belongs to their organization
      let userOrgId = user.organization;
      if (typeof userOrgId === 'object' && (userOrgId as any)?._id) {
        userOrgId = (userOrgId as any)._id;
      }

      return userOrgId === organizationId;
    });

    return filteredUsers;
  }, [usersData?.data, organizationId, currentUser?.role]);

  const addMembersMutation = useAddGroupMembers({
    onSuccess: (data) => {
      refetchMembers();
      onRefetchGroups?.(); // Refetch groups to update member count and list
      setSelectedUsers([]);
      setSearchTerm('');
      clearGlobalError();
    },
    onError: (error: any) => {
      console.error('addMembersMutation ERROR:', error);
      setGlobalError(error.message || 'Failed to add members');
    }
  });

  const removeMemberMutation = useRemoveGroupMember({
    onSuccess: (data) => {
      refetchMembers();
      onRefetchGroups?.(); // Refetch groups to update member count and list
      clearGlobalError();
    },
    onError: (error: any) => {
      console.error('removeMemberMutation ERROR:', error);
      setGlobalError(error.message || 'Failed to remove member');
    }
  });

  // Filter available users (not already members)
  const availableUsers = useMemo(() => {
    if (!secureUsers) return [];

    const memberIds = currentMembers.map(m => m._id || m.id).filter(Boolean) as string[];
    return secureUsers.filter(user => {
      const userId = user._id || user.id;
      return userId && !memberIds.includes(userId);
    });
  }, [secureUsers, currentMembers]);

  const handleAddMembers = () => {

    if (selectedUsers.length === 0) {
      setGlobalError('Please select at least one user to add');
      return;
    }

    if (!group?._id && !group?.id) {
      setGlobalError('Group ID is missing');
      return;
    }

    const groupId = group._id || group.id;


    addMembersMutation.mutate({
      organizationId: organizationId!,
      groupId: groupId!,
      memberData: {
        userIds: selectedUsers
      }
    });
  };

  const handleRemoveMember = (userId: string) => {

    if (!group?._id && !group?.id) {
      setGlobalError('Group ID is missing');
      return;
    }

    const groupId = group._id || group.id;


    removeMemberMutation.mutate({
      organizationId: organizationId!,
      groupId: groupId!,
      userId
    });
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (!isOpen) return null;

  const isLoading = usersLoading;
  const isProcessing = addMembersMutation.loading || removeMemberMutation.loading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header - Enhanced with gradient */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Manage Members
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {group?.name} • {group?.type === 'visibility' ? 'Visibility Group' : 'Approval Group'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - Improved layout with grid */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading users...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
              {/* Current Members Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Current Members
                  </h3>
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-full">
                    {currentMembers.length}
                  </span>
                </div>

                {currentMembers.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center py-8">
                      <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400 italic">No members in this group</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add members from the list on the right</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                    {currentMembers.map((member) => {
                      const memberId = member._id || member.id;
                      if (!memberId) return null;

                      return (
                        <div
                          key={memberId}
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-100 dark:border-blue-800 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md flex-shrink-0">
                              {member.username?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 dark:text-white truncate">
                                {member.username || member.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{member.role}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const memberId = member._id || member.id;
                              if (memberId) {
                                handleRemoveMember(memberId);
                              } else {
                                setGlobalError('Member ID is missing');
                              }
                            }}
                            disabled={isProcessing}
                            className="ml-3 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition-colors rounded-md flex-shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add Members Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Add New Members
                  </h3>
                  {selectedUsers.length > 0 && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium rounded-full">
                      {selectedUsers.length} selected
                    </span>
                  )}
                </div>

                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <Input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10"
                    />
                  </div>
                </div>

                {/* Available Users List */}
                {availableUsers.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center py-8">
                      <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {searchTerm ? 'No users found matching your search' : 'No available users to add'}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                    {availableUsers.map((user) => {
                      const userId = user._id || user.id;
                      if (!userId) return null;
                      const isSelected = selectedUsers.includes(userId);

                      return (
                        <div
                          key={userId}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all",
                            isSelected
                              ? "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 shadow-md"
                              : "bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent"
                          )}
                          onClick={() => handleUserToggle(userId)}
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md flex-shrink-0",
                              isSelected
                                ? "bg-gradient-to-br from-green-500 to-emerald-600"
                                : "bg-gradient-to-br from-gray-400 to-gray-600"
                            )}>
                              {user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 dark:text-white truncate">
                                {user.username || user.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center ml-3 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleUserToggle(userId)}
                              // The row toggles on click too. Without this the
                              // checkbox's own change and the row's click both
                              // fired — tick then untick — so clicking the
                              // checkbox appeared to do nothing (2026-09-28).
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${user.username || user.email || 'user'}`}
                              className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer - Enhanced with better spacing */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center">
            {selectedUsers.length > 0 && (
              <div className="flex items-center px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={selectedUsers.length === 0 || isProcessing}
              loading={addMembersMutation.loading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Add {selectedUsers.length > 0 ? `${selectedUsers.length} ` : ''}Member{selectedUsers.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN GROUPS PAGE COMPONENT =====

export const GroupsPage: React.FC = () => {
  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | undefined>();
  const [managingGroup, setManagingGroup] = useState<Group | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'visibility' | 'approval'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize selectedOrganization from URL query param
  const [selectedOrganization, setSelectedOrganization] = useState(
    searchParams.get('org') || ''
  );

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError, addNotification } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Organization strategy with proper context resolution
  const organizationStrategy = useMemo((): OrganizationStrategy => {
    if (currentUser?.role === 'implementor') {
      return {
        mode: 'organization_specific',
        canSelectOrganization: true,
        defaultOrganization: selectedOrganization || undefined
      };
    } else {
      // For admin/contributor, restrict to their organization
      let userOrgId: string | undefined;

      if (currentUser?.organization) {
        if (typeof currentUser.organization === 'string') {
          userOrgId = currentUser.organization;
        } else if ((currentUser.organization as any)._id) {
          userOrgId = (currentUser.organization as any)._id;
        }
      }

      return {
        mode: 'single_organization',
        canSelectOrganization: false,
        defaultOrganization: userOrgId
      };
    }
  }, [currentUser?.role, currentUser?.organization, selectedOrganization]);

  // Determine the organization ID to use for API calls
  const groupsOrgId = useMemo(() => {
    if (organizationStrategy.mode === 'organization_specific') {
      return selectedOrganization && selectedOrganization.trim() !== '' ? selectedOrganization : undefined;
    } else {
      return organizationStrategy.defaultOrganization;
    }
  }, [organizationStrategy, selectedOrganization]);

  // Permissions based on role
  const permissions = useMemo(() => {
    if (!currentUser) return { canView: false, canCreate: false, canEdit: false, canDeactivate: false, canDelete: false, canManageMembers: false };

    switch (currentUser.role) {
      case 'implementor':
        return { canView: true, canCreate: true, canEdit: true, canDeactivate: true, canDelete: true, canManageMembers: true };
      case 'admin':
        return { canView: true, canCreate: true, canEdit: true, canDeactivate: true, canDelete: true, canManageMembers: true };
      case 'contributor':
        return { canView: true, canCreate: false, canEdit: false, canDeactivate: false, canDelete: false, canManageMembers: false };
      default:
        return { canView: false, canCreate: false, canEdit: false, canDeactivate: false, canDelete: false, canManageMembers: false };
    }
  }, [currentUser?.role]);

  // Load organizations for implementors
  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError
  } = useOrganizations(
    currentUser?.role === 'implementor' ? {} : undefined,
    { immediate: currentUser?.role === 'implementor' }
  );

  const organizations = organizationsData?.data || [];

  // Only load groups when we have a valid organization ID
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  const groupsParams = useMemo((): GroupsQueryParams | undefined => {
    if (!groupsOrgId) return undefined;

    return {
      search: debouncedSearchTerm || undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      limit: 50,
      includeAssignments: true  // Request assignment data from backend
    };
  }, [debouncedSearchTerm, typeFilter, statusFilter, groupsOrgId]);

  const {
    data: groupsData,
    loading,
    error,
    refetch
  } = useGroups(
    groupsOrgId,
    groupsParams,
    {
      immediate: !!groupsOrgId
    }
  );

  // Mutations
  const createGroupMutation = useCreateGroup({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingGroup(undefined);
      refetch();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to create group');
    }
  });

  const updateGroupMutation = useUpdateGroup({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingGroup(undefined);
      refetch();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to update group');
    }
  });

  const deleteGroupMutation = useDeleteGroup({
    onSuccess: (data: any) => {
      refetch();
      clearGlobalError();

      // Display warnings if the group was assigned to projects/workflows
      if (data?.warnings && data.warnings.length > 0) {
        const warningMessage = [
          'Group deleted successfully.',
          '',
          'Warnings:',
          ...data.warnings
        ].join('\n');

        addNotification({ type: 'warning', title: 'Group Deleted', message: warningMessage });
      }
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to delete group');
    }
  });

  // Get display context
  const getDisplayContext = useCallback(() => {
    if (organizationStrategy.mode === 'organization_specific') {
      if (selectedOrganization) {
        const org = organizations.find(o => (o as any)._id === selectedOrganization || o.id === selectedOrganization);
        return org?.name || 'Selected Organization';
      }
      return 'No Organization Selected';
    } else {
      if (currentUser?.organization) {
        if (typeof currentUser.organization === 'string') {
          const org = organizations.find(o => (o as any)._id === currentUser.organization);
          return org?.name || 'Your Organization';
        }
        return (currentUser.organization as any).name || 'Your Organization';
      }
      return 'Unknown Organization';
    }
  }, [organizationStrategy, selectedOrganization, organizations, currentUser]);

  // Event handlers
  const handleCreateGroup = useCallback(() => {
    setEditingGroup(undefined);
    setIsModalOpen(true);
  }, []);

  const handleEditGroup = useCallback((group: Group) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(async (group: Group) => {
    if (!groupsOrgId) return;

    const groupId = (group as any)._id || group.id;
    const newStatus = !group.isActive;

    const actionText = newStatus ? 'activate' : 'deactivate';
    const confirmed = await confirm({
      title: `${newStatus ? 'Activate' : 'Deactivate'} Group`,
      message: `Are you sure you want to ${actionText} this group?`,
      confirmText: newStatus ? 'Activate' : 'Deactivate',
      variant: 'warning',
    });
    if (confirmed) {
      updateGroupMutation.mutate({
        organizationId: groupsOrgId,
        groupId,
        data: { isActive: newStatus }
      });
    }
  }, [groupsOrgId, updateGroupMutation, confirm]);

  const handleDeleteGroup = useCallback(async (group: Group) => {
    if (!groupsOrgId) return;

    const groupId = (group as any)._id || group.id;

    // Check if group is active
    if (group.isActive) {
      setGlobalError('Cannot delete active group. Please deactivate it first.');
      return;
    }

    // Check if group is system group
    if ((group as any).isSystemGroup) {
      setGlobalError('Cannot delete system group.');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Group',
      message: 'Are you sure you want to delete this group? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteGroupMutation.mutate({ organizationId: groupsOrgId, groupId });
    }
  }, [groupsOrgId, deleteGroupMutation, setGlobalError, confirm]);

  const handleManageMembers = useCallback((group: Group) => {
    setManagingGroup(group);
    setIsMemberModalOpen(true);
  }, []);

  // Leave group mutation for contributors
  const leaveGroupMutation = useLeaveGroup({
    onSuccess: () => {
      refetch();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to leave group');
    }
  });

  const handleLeaveGroup = useCallback(async (group: Group) => {
    if (!groupsOrgId) return;

    const groupId = (group as any)._id || group.id;
    const groupName = group.name;

    // Check if group allows leaving
    if (group.permissions?.canLeaveSelf === false) {
      setGlobalError('This group does not allow members to leave on their own. Please contact an administrator.');
      return;
    }

    const confirmed = await confirm({
      title: 'Leave Group',
      message: `Are you sure you want to leave the group "${groupName}"?`,
      confirmText: 'Leave',
      variant: 'warning',
    });
    if (confirmed) {
      leaveGroupMutation.mutate({
        organizationId: groupsOrgId,
        groupId
      });
    }
  }, [groupsOrgId, leaveGroupMutation, setGlobalError, confirm]);

  const handleFormSubmit = useCallback((formData: GroupFormData) => {
    if (!groupsOrgId) {
      setGlobalError('Organization context is required');
      return;
    }

    const request: GroupCreateRequest | GroupUpdateRequest = {
      name: formData.name.trim(),
      description: formData.description?.trim() || '',
      ...(editingGroup ? {} : { type: formData.type }), // Only include type for creation
      isActive: formData.isActive
    };

    if (editingGroup) {
      updateGroupMutation.mutate({
        organizationId: groupsOrgId,
        groupId: (editingGroup as any)._id || editingGroup.id,
        data: request as GroupUpdateRequest
      });
    } else {
      createGroupMutation.mutate({
        organizationId: groupsOrgId,
        data: request as GroupCreateRequest
      });
    }
  }, [groupsOrgId, editingGroup, createGroupMutation, updateGroupMutation, setGlobalError]);

  // Extract data from response - MUST be before any conditional returns
  const groups = groupsData?.data || [];
  const currentContext = getDisplayContext();

  // Debug logging to check what groups are being returned
  React.useEffect(() => {
    if (groups.length > 0) {
    }
  }, [groups, currentUser]);

  // Filter groups by status on frontend (in case backend doesn't support it yet)
  const filteredGroups = useMemo(() => {
    if (statusFilter === 'all') return groups;
    if (statusFilter === 'active') return groups.filter(g => g.isActive !== false);
    if (statusFilter === 'inactive') return groups.filter(g => g.isActive === false);
    return groups;
  }, [groups, statusFilter]);

  // Page actions
  const actions = useMemo(() => {
    const actionButtons = [];

    if (permissions.canCreate && groupsOrgId) {
      actionButtons.push(
        <Button
          key="create"
          onClick={handleCreateGroup}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Create Group
        </Button>
      );
    }

    return actionButtons;
  }, [permissions.canCreate, groupsOrgId, handleCreateGroup]);

  // Loading state for organizations
  if (currentUser?.role === 'implementor' && orgsLoading) {
    return (
      <PageLayout title="Groups" subtitle="Loading...">
        <div className="space-y-6">
          <div className="flex justify-center py-8">
            <LoadingSpinner />
            <p className="ml-3 text-gray-400">Loading organizations...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Error state for organizations
  if (currentUser?.role === 'implementor' && orgsError) {
    return (
      <PageLayout title="Groups" subtitle="Error loading organizations">
        <div className="space-y-6">
          <ErrorAlert
            message={`Failed to load organizations: ${orgsError}`}
            onClose={() => {}}
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <GroupsErrorBoundary>
      <PageLayout
        title="Groups"
        subtitle={`${currentContext} - User groups and permissions`}
        actions={actions}
        loading={loading || orgsLoading}
      >
        <div className="space-y-6">
          {/* Organization Selection for Implementors */}
          {organizationStrategy.canSelectOrganization && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center space-x-4">
                <label htmlFor="organization-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Organization:
                </label>
                <select
                  id="organization-select"
                  value={selectedOrganization}
                  onChange={(e) => {
                    const newOrg = e.target.value;
                    setSelectedOrganization(newOrg);
                    // Update URL params
                    if (newOrg) {
                      setSearchParams({ org: newOrg });
                    } else {
                      setSearchParams({});
                    }
                  }}
                  className="flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Organization...</option>
                  {organizations.map((org) => (
                    <option key={(org as any)._id || org.id} value={(org as any)._id || org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
              {!selectedOrganization && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                  Please select an organization to view and manage groups.
                </p>
              )}
            </div>
          )}

          {/* Filters Section - Consistent with Users/Orgs pages */}
          {groupsOrgId && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-wrap items-end gap-4">
                {/* Search */}
                <div className="flex-1 min-w-64">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search
                  </label>
                  <Input
                    type="text"
                    placeholder="Search groups..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                {/* Type Filter */}
                <div className="w-48">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type
                  </label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="visibility">Visibility</option>
                    <option value="approval">Approval</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div className="w-48">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Clear Filters Button */}
                <div>
                  <Button
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                      setStatusFilter('all');
                    }}
                    variant="outline"
                    className="whitespace-nowrap"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Groups Content */}
          {!groupsOrgId ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                {organizationStrategy.canSelectOrganization
                  ? 'Select an organization above to view groups.'
                  : 'No organization context available.'}
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
              <p className="ml-3 text-gray-400">Loading groups...</p>
            </div>
          ) : error ? (
            <ErrorAlert
              message={error}
              onClose={() => clearGlobalError()}
            />
          ) : filteredGroups.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                  ? 'No groups found matching your filters.'
                  : 'No groups exist in this organization yet.'}
              </p>
              {permissions.canCreate && !searchTerm && typeFilter === 'all' && statusFilter === 'all' && (
                <Button
                  onClick={handleCreateGroup}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Create First Group
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <GroupCard
                  key={(group as any)._id || group.id}
                  group={group as EnhancedGroup}
                  currentUser={currentUser}
                  permissions={permissions}
                  organizationId={groupsOrgId}
                  onEdit={handleEditGroup}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDeleteGroup}
                  onManageMembers={handleManageMembers}
                  onLeaveGroup={handleLeaveGroup}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modals */}
        <GroupFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingGroup(undefined);
          }}
          group={editingGroup}
          organizationId={groupsOrgId || ''}
          organizationName={currentContext}
          onSubmit={handleFormSubmit}
          loading={createGroupMutation.loading || updateGroupMutation.loading}
        />

        <MemberManagementModal
          isOpen={isMemberModalOpen}
          onClose={() => {
            setIsMemberModalOpen(false);
            setManagingGroup(undefined);
          }}
          group={managingGroup}
          organizationId={groupsOrgId}
          onRefetchGroups={refetch}
        />
      </PageLayout>
    </GroupsErrorBoundary>
  );
};

// ===== GROUP CARD COMPONENT =====
const GroupCard: React.FC<{
  group: EnhancedGroup;
  currentUser: any;
  permissions: any;
  organizationId?: string;
  onEdit: (group: Group) => void;
  onToggleActive: (group: Group) => void;
  onDelete: (group: Group) => void;
  onManageMembers: (group: Group) => void;
  onLeaveGroup?: (group: Group) => void;
}> = memo(({ group, currentUser, permissions, organizationId, onEdit, onToggleActive, onDelete, onManageMembers, onLeaveGroup }) => {
  const canEdit = useMemo(() => {
    if (!permissions.canEdit) return false;
    if ((group as any).isSystemGroup && currentUser?.role !== 'implementor') return false;
    return true;
  }, [permissions.canEdit, currentUser?.role, group]);

  const canDeactivate = useMemo(() => {
    if (!permissions.canDeactivate) return false;
    if ((group as any).isSystemGroup) return false;
    return true;
  }, [permissions.canDeactivate, group]);

  const canDelete = useMemo(() => {
    if (!permissions.canDelete) return false;
    if ((group as any).isSystemGroup) return false;
    if (group.isActive) return false; // Can only delete inactive groups
    return true;
  }, [permissions.canDelete, group]);

  // Check if current user can leave the group
  const canLeaveGroup = useMemo(() => {
    // Only contributors can see the leave button (admins/implementors use member management)
    if (currentUser?.role !== 'contributor') return false;

    // Check if user is a member of this group
    const userId = currentUser._id;
    const isMember = group.members?.some((member: any) => {
      const memberId = typeof member === 'string' ? member : (member._id || member.id);
      return memberId === userId;
    });

    if (!isMember) return false;

    // Check if group allows self-leaving (default to true if not specified)
    if (group.permissions?.canLeaveSelf === false) {
      return false;
    }

    return true;
  }, [currentUser, group]);

  const typeConfig = useMemo(() => {
    const configs: Record<'visibility' | 'approval', { color: string; icon: string; description: string }> = {
      visibility: {
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
        icon: '👁',
        description: 'Controls access to projects and dashboards'
      },
      approval: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
        icon: '✓',
        description: 'Used for response approval workflows'
      }
    };
    return configs[group.type as keyof typeof configs] || {
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      icon: '?',
      description: 'Unknown group type'
    };
  }, [group.type]);

  // Debug logging for assignments

  return (
    <div className={cn(
      "bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 transition-all",
      group.isActive
        ? "border-gray-200 dark:border-gray-700"
        : "border-red-200 dark:border-red-800 bg-gray-50 dark:bg-gray-900/50"
    )}>
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {group.name}
            </h3>
            {(group as any).isSystemGroup && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                System
              </span>
            )}
            {!group.isActive && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                Inactive
              </span>
            )}
          </div>
          {group.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {truncateText(group.description, 100)}
            </p>
          )}
        </div>

        <span className={cn('px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1', typeConfig.color)}>
          <span>{typeConfig.icon}</span>
          <span>{group.type}</span>
        </span>
      </div>

      {/* Stats */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          {(group as any).memberCount || (Array.isArray(group.members) ? group.members.length : 0)} members
        </div>

        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Created {formatDate(group.createdAt)}
        </div>
      </div>

      {/* Assignments */}
      {group.type === 'visibility' && group.assignedProjects && group.assignedProjects.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Assigned Projects ({group.assignedProjects.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {group.assignedProjects.slice(0, 3).map(project => (
              <span key={project._id} className="text-xs bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 px-2 py-1 rounded border border-blue-200 dark:border-blue-700">
                {truncateText(project.name, 20)}
              </span>
            ))}
            {group.assignedProjects.length > 3 && (
              <span className="text-xs text-blue-700 dark:text-blue-300 px-2 py-1">
                +{group.assignedProjects.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {group.type === 'approval' && group.assignedWorkflows && group.assignedWorkflows.length > 0 && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center text-sm font-medium text-green-700 dark:text-green-300 mb-2">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Assigned Workflows ({group.assignedWorkflows.length})
          </div>
          <div className="space-y-1">
            {group.assignedWorkflows.slice(0, 2).map(workflow => (
              <div key={workflow._id} className="text-xs bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 px-2 py-1 rounded border border-green-200 dark:border-green-700">
                <div className="font-medium">{truncateText(workflow.name, 25)}</div>
                {workflow.stages && workflow.stages.length > 0 && (
                  <div className="text-green-600 dark:text-green-400 mt-0.5">
                    Stages: {workflow.stages.map(s => s.stageName).join(', ')}
                  </div>
                )}
              </div>
            ))}
            {group.assignedWorkflows.length > 2 && (
              <span className="text-xs text-green-700 dark:text-green-300 block">
                +{group.assignedWorkflows.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {permissions.canManageMembers && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onManageMembers(group)}
            className="flex-1"
          >
            Members
          </Button>
        )}

        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(group)}
          >
            Edit
          </Button>
        )}

        {canDeactivate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleActive(group)}
            className={group.isActive ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
          >
            {group.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        )}

        {canDelete && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(group)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Can only delete inactive groups"
          >
            Delete
          </Button>
        )}

        {canLeaveGroup && onLeaveGroup && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onLeaveGroup(group)}
            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            title={
              group.permissions?.canLeaveSelf === false
                ? "This group does not allow members to leave. Contact an administrator."
                : "Leave this group"
            }
          >
            Leave Group
          </Button>
        )}
      </div>
    </div>
  );
});

// ===== GROUP FORM MODAL =====
const GroupFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  group?: Group;
  organizationId: string;
  organizationName: string;
  onSubmit: (data: GroupFormData) => void;
  loading?: boolean;
}> = ({ isOpen, onClose, group, organizationId, organizationName, onSubmit, loading = false }) => {
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    type: 'visibility',
    description: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Partial<GroupFormData>>({});

  // Initialize form data when group changes
  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        type: (group.type === 'system' ? 'visibility' : group.type as 'visibility' | 'approval') || 'visibility',
        description: group.description || '',
        isActive: group.isActive !== false
      });
    } else {
      setFormData({
        name: '',
        type: 'visibility',
        description: '',
        isActive: true
      });
    }
    setErrors({});
  }, [group, isOpen]);

  // Input change handler with validation
  const handleInputChange = useCallback((field: keyof GroupFormData) => {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };
  }, [errors]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<GroupFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Group name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Group name must be less than 100 characters';
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  }, [formData, validateForm, onSubmit]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {group ? 'Edit Group' : 'Create Group'}
              </h2>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                Organization: {organizationName}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Group Name */}
            <div>
              <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group Name *
              </label>
              <Input
                id="groupName"
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder="Enter group name..."
                className={cn(
                  'w-full',
                  errors.name && 'border-red-500 focus:border-red-500'
                )}
                disabled={loading}
                maxLength={100}
                required
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.name.length}/100 characters
              </p>
            </div>

            {/* Group Type - Only show for new groups */}
            {!group && (
              <div>
                <label htmlFor="groupType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Type *
                </label>
                <select
                  id="groupType"
                  value={formData.type}
                  onChange={handleInputChange('type')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                  required
                >
                  <option value="visibility">Visibility Group</option>
                  <option value="approval">Approval Group</option>
                </select>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <p className="mb-1">
                    <strong>Note:</strong> Type cannot be changed after creation.
                  </p>
                  <p>
                    <strong>{formData.type === 'visibility' ? 'Visibility:' : 'Approval:'}</strong>{' '}
                    {formData.type === 'visibility'
                      ? 'Controls access to projects and dashboards'
                      : 'Used for response approval workflows'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Show current type for existing groups */}
            {group && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Type
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md">
                  <span className="text-gray-900 dark:text-white capitalize">{group.type}</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {group.type === 'visibility'
                      ? 'Controls access to projects and dashboards'
                      : 'Used for response approval workflows'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label htmlFor="groupDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="groupDescription"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Enter group description..."
                rows={3}
                className={cn(
                  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical',
                  errors.description && 'border-red-500 focus:border-red-500'
                )}
                disabled={loading}
                maxLength={500}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                id="groupActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={handleInputChange('isActive')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="groupActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Group is active
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {group ? 'Update Group' : 'Create Group'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
